import { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';
import { Archive, Copy, X } from 'lucide-react';

import { PAGES } from '../../constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { ActionBar } from 'components/ActionBar';
import { CreateMicrostoreColumnTemplatePopup } from './Popups/CreateMicrostoreColumnTemplatePopup';
import { ArchiveMicrostoreColumnTemplatesPopup } from './Popups/ArchiveMicrostoreColumnTemplatesPopup';
import { ReplaceMicrostoreColumnTemplateProductsPopup } from './Popups/ReplaceMicrostoreColumnTemplateProductsPopup';
import { MicrostoreColumnTemplatesList } from './components/MicrostoreColumnTemplatesList';
import { useGetMicrostoreColumnTemplatesQuery, useDuplicateMicrostoreColumnTemplateMutation } from 'data/__generated__';

export const MicrostoreColumnTemplates = () => {
    const [selectedMicrostoreColumnTemplateIds, setSelectedMicrostoreColumnTemplateIds] = useState<string[]>([]);

    const [isCreateMicrostoreColumnTemplatePopupOpen, setIsCreateMicrostoreColumnTemplatePopupOpen] =
        useState<boolean>(false);
    const [isArchiveMicrostoreColumnTemplatesPopupOpen, setIsArchiveMicrostoreColumnTemplatesPopupOpen] =
        useState<boolean>(false);
    const [isReplaceProductsPopupOpen, setIsReplaceProductsPopupOpen] = useState<boolean>(false);

    const { data: microstoreColumnTemplatesData } = useGetMicrostoreColumnTemplatesQuery();

    const [duplicateMicrostoreColumnTemplate] = useDuplicateMicrostoreColumnTemplateMutation();

    const handleDuplicateTemplate = async () => {
        if (selectedMicrostoreColumnTemplateIds.length !== 1) return;

        const templateId = selectedMicrostoreColumnTemplateIds[0];

        const { data } = await duplicateMicrostoreColumnTemplate({
            variables: { microstoreColumnTemplateId: templateId },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la duplication du template');
        }

        const {
            duplicateMicrostoreColumnTemplateMutation: { newMicrostoreColumnTemplate, errors },
        } = data;

        if (errors.length) {
            errors.forEach((error) => {
                toast.error(`Erreur : ${error}`);
            });
            return;
        }

        if (newMicrostoreColumnTemplate) {
            toast.success('Template dupliqué avec succès');

            const newWindow = window.open(
                `/microstore-column-template/${newMicrostoreColumnTemplate._id}`,
                '_blank',
                'noopener,noreferrer',
            );
            if (newWindow) {
                newWindow.opener = null;
            }
        }

        setSelectedMicrostoreColumnTemplateIds([]);
    };

    const microstoreColumnTemplates = microstoreColumnTemplatesData?.microstoreColumnTemplatesQuery ?? [];

    const actionSections = [
        {
            title: 'Actions',
            actions: [
                {
                    icon: <Copy />,
                    label: 'Dupliquer le template sélectionné',
                    onClick: handleDuplicateTemplate,
                    hidden: selectedMicrostoreColumnTemplateIds.length !== 1,
                    dataTest: 'duplicate-template-action',
                },
                {
                    icon: <X />,
                    label: 'Tout désélectionner',
                    onClick: () => {
                        setSelectedMicrostoreColumnTemplateIds([]);
                    },
                    hidden: !selectedMicrostoreColumnTemplateIds.length,
                },
                {
                    icon: <Archive />,
                    label: 'Archiver les templates sélectionnés',
                    onClick: () => {
                        setIsArchiveMicrostoreColumnTemplatesPopupOpen(true);
                    },
                    hidden: !selectedMicrostoreColumnTemplateIds.length,
                },
            ],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.microstoreColumnTemplates} />
                </HeaderTitle>
                <ActionContainer>
                    <TotemPrimaryButton
                        onClick={() => {
                            setIsReplaceProductsPopupOpen(true);
                        }}
                    >
                        Remplacer les produits
                    </TotemPrimaryButton>
                    <TotemPrimaryButton
                        onClick={() => {
                            setIsCreateMicrostoreColumnTemplatePopupOpen(true);
                        }}
                    >
                        Créer un template de colonne
                    </TotemPrimaryButton>
                </ActionContainer>
            </Header>
            <ContentContainer>
                <Content>
                    <MicrostoreColumnTemplatesList
                        selectedKeys={selectedMicrostoreColumnTemplateIds}
                        onSelectionChange={setSelectedMicrostoreColumnTemplateIds}
                    />
                </Content>
                <ActionBar
                    actionSections={actionSections}
                    isInitiallyOpen={!!selectedMicrostoreColumnTemplateIds.length}
                />
            </ContentContainer>
            <CreateMicrostoreColumnTemplatePopup
                isOpen={isCreateMicrostoreColumnTemplatePopupOpen}
                setIsOpen={setIsCreateMicrostoreColumnTemplatePopupOpen}
            />
            <ArchiveMicrostoreColumnTemplatesPopup
                microstoreColumnTemplates={microstoreColumnTemplates}
                selectedMicrostoreColumnTemplateIds={selectedMicrostoreColumnTemplateIds}
                setSelectedMicrostoreColumnTemplateIds={setSelectedMicrostoreColumnTemplateIds}
                isOpen={isArchiveMicrostoreColumnTemplatesPopupOpen}
                setIsOpen={setIsArchiveMicrostoreColumnTemplatesPopupOpen}
            />
            <ReplaceMicrostoreColumnTemplateProductsPopup
                isOpen={isReplaceProductsPopupOpen}
                setIsOpen={setIsReplaceProductsPopupOpen}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ContentContainer = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: hidden;
`;

const ActionContainer = styled.div`
    display: flex;
    align-items: center;
    & > :not(:first-child) {
        margin-left: 10px;
    }
`;
